/** @jsx h */
import { h } from 'preact'
import { useState } from 'preact/compat'
import dayjs from 'dayjs'
import Icon from './icons/icons'
import clsx from 'clsx'

const generateDateRows = date => {
  const year = date.year()
  const daysInMonth = date.daysInMonth()
  const rows = [[]]
  let rowIndex = 0

  // fill in initial rows
  ;[...Array(daysInMonth)].forEach((_, num) => {
    const temp = dayjs(new Date(year, date.month(), num + 1))
    const dow = temp.weekday()

    rows[rowIndex].push(temp)
    if (dow === 6 && num !== daysInMonth - 1) {
      rowIndex += 1
      rows.push([])
    }
  })

  const rowLen = rows.length
  const firstRow = rows[0]
  const lastRow = rows[rowLen - 1]

  // backfill days from prev month in first week of month
  if (firstRow.length !== 7) {
    const backfillBy = 7 - firstRow.length
    const first = firstRow[0]

    ;[...Array(backfillBy)].forEach((_, i) => {
      const d = first.subtract(i + 1, 'day')
      firstRow.splice(0, 0, d)
    })
  }

  // frontfill days from next month in last week of month
  if (lastRow.length !== 7) {
    const fillBy = 7 - lastRow.length
    const lastLen = lastRow.length
    const last = lastRow[lastLen - 1]

    ;[...Array(fillBy)].forEach((_, i) => {
      const d = last.add(i + 1, 'day')
      lastRow.push(d)
    })
  }

  return rows
}

const DayCell = ({ date, current, selectedDate, onClick, availabilities, openingHours, ...others }) => {
  const d = date
  const today = dayjs()
  const availableDay = availabilities?.find(el => el?.recordDate === date.locale('en-gb').format('YYYY-MM-DD'))
  const isDisabled =
    date.isBefore(today.add(1, 'day')) || date.isAfter(today.add(3, 'month')) || !availableDay || availableDay.isClosed

  const { className, ...rest } = others

  let klassName = clsx('rcc-p-2 rcc-text-center rcc-font-normal rcc-relative rcc-h-[50px]', {
    'rcc-c-selected-day': d.isSame(selectedDate),
    'rcc-c-today-cell rcc-text-text': d.isSame(today, 'day'),
    'rcc-text-gray-very-light': isDisabled,
    'rcc-c-day-cell rcc-text-text rcc-cursor-pointer': !isDisabled
  })

  if (className) klassName += ` ${className}`

  const props = {
    onClick: e => {
      e.preventDefault()
      !isDisabled && onClick(d)
    },
    onkeydown: e => {
      if (e.keyCode === 13 || e.keyCode === 32) {
        e.preventDefault()
        !isDisabled && onClick(d)
      }
    },
    disabled: isDisabled ? 'disabled' : undefined,
    className: klassName,
    ...rest
  }
  return (
    <td
      {...props}
      tabIndex={props.disabled ? '-1' : '0'}
      role='button'
      aria-disabled={props.disabled}
      aria-pressed={d.isSame(selectedDate)}
    >
      {d.format('D')}
    </td>
  )
}

const DatePicker = ({ value, onChange, availabilities }) => {
  const [date, setDate] = useState((value && dayjs(value)) || dayjs())
  const [selectedDate, setSelectDate] = useState(value && dayjs(value))
  const rows = generateDateRows(date)
  const weekDayLabels = dayjs.weekdaysMin().map(el => el.toUpperCase())
  const firstDayOfWeek = dayjs.localeData().firstDayOfWeek()

  // ordering the labels to start by the first day of the week (depends on the locale)
  ;[...Array(firstDayOfWeek)].forEach(() => weekDayLabels.push(weekDayLabels.shift()))

  const clickedDate = date => {
    setSelectDate(date.clone())
    onChange(date.format('YYYY-MM-DD'))
  }

  const changeMonth = by => {
    const today = dayjs()

    let newDate
    if (by > 0) {
      newDate = date.clone().add(1, 'month')
      if (newDate.diff(today, 'month') < 3) {
        setDate(newDate)
      }
    } else {
      newDate = date.clone().subtract(1, 'month')
      if (newDate.diff(today, 'month') > -1) {
        setDate(newDate)
      }
    }
  }

  const monthYearFormat = () => {
    if (dayjs.locale().includes('zh')) {
      return date.format('YYYY年MM月')
    }

    if (dayjs.locale() === 'ko') {
      return date.format('MMMM YYYY년')
    }

    return date.format('MMMM YYYY')
  }

  return (
    <div>
      <div className='rcc-flex rcc-justify-between rcc-items-center rcc-border-b rcc-border-gray-light rcc-pb-3.5 rcc-mb-2.5'>
        <button
          type='button'
          className='rcc-bg-transparent rcc-cursor-pointer'
          aria-label='Previous month'
          onClick={e => changeMonth(-1)}
        >
          <Icon name='arrow-prev' />
        </button>
        <p className='rcc-uppercase rcc-tracking-widest'>{monthYearFormat()}</p>
        <button
          type='button'
          className='rcc-bg-transparent rcc-cursor-pointer'
          aria-label='Next month'
          onClick={e => changeMonth(1)}
        >
          <Icon name='arrow-next' />
        </button>
      </div>

      <table className='rcc-w-full' cellSpacing='0' cellPadding='0'>
        <thead>
          <tr>
            {weekDayLabels.map(label => (
              <th className='rcc-p-2' key={label}>
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {row.map((d, i) => (
                <DayCell
                  availabilities={availabilities}
                  date={d}
                  current={date}
                  selectedDate={selectedDate}
                  key={d}
                  onClick={clickedDate}
                />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
export default DatePicker
